import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError, downloadCvPdf, downloadCvDocx } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import FieldRenderer from "@/components/FieldRenderer";
import CVDocument from "@/components/CVDocument";
import AiAssistant from "@/components/AiAssistant";
import { ACCENT_PRESETS } from "@/lib/cvStyle";
import { SwishQuickPay } from "@/components/SwishQuickPay";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Save, RotateCcw, Printer, CheckCircle2, ArrowLeft, Eye, EyeOff,
  ChevronUp, ChevronDown, Plus, Trash2, AlertTriangle, Download, FileType,
  ClipboardCheck, ShieldCheck, CircleCheck, CircleAlert, Info, Lightbulb, Sparkles, Quote, Mail, ArrowRight, Lock, ExternalLink, Camera,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const TAG_SECTIONS = [
  ["core_competencies", "Kärnkompetenser"],
  ["systems_tools", "System och verktyg"],
  ["certifications", "Certifieringar"],
  ["languages", "Språk"],
  ["personal_strengths", "Personliga styrkor"],
];

export default function CVEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cv, setCv] = useState(null);
  const [c, setC] = useState(null);
  const [contact, setContact] = useState({});
  const [template, setTemplate] = useState("modern");
  const [accentColor, setAccentColor] = useState("");
  const [layout, setLayout] = useState("standard");
  const [photoData, setPhotoData] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dl, setDl] = useState(null);
  const [qcOpen, setQcOpen] = useState(false);
  const [qc, setQc] = useState(null);
  const [qcLoading, setQcLoading] = useState(false);
  const [highlights, setHighlights] = useState(null);
  const [hlLoading, setHlLoading] = useState(false);
  const [refStrengths, setRefStrengths] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/references/responses").catch(() => ({ data: [] })),
      api.get("/references").catch(() => ({ data: [] })),
      api.get("/character/dimensions").catch(() => ({ data: [] })),
    ]).then(([resp, refs, dims]) => {
      const names = Object.fromEntries((refs.data || []).map((r) => [r.id, r.name]));
      const titles = Object.fromEntries((dims.data || []).map((d) => [d.id, d.title]));
      const out = [];
      (resp.data || []).forEach((r) => {
        Object.entries(r.dimensions || {}).forEach(([did, a]) => {
          if (a && !a.cannot_assess && (a.text || "").trim()) {
            out.push({ dimension: titles[did] || did, text: a.text, name: names[r.reference_id] || "Referens" });
          }
        });
      });
      setRefStrengths(out);
    });
  }, [id]);

  const insertRefStrength = (s) => {
    const line = `${s.dimension}: ”${s.text}” — bekräftad av ${s.name}`;
    const cur = Array.isArray(c.reference_verified) ? c.reference_verified : [];
    if (cur.includes(line)) { toast.info("Redan tillagd i CV:t"); return; }
    setField("reference_verified", [...cur, line]);
    toast.success("Referensbekräftad styrka tillagd under Verifierat av referenser");
  };

  const genHighlights = async () => {
    setHlLoading(true);
    try {
      await save({}, true);
      const { data } = await api.post(`/cvs/${id}/highlights`);
      setHighlights(data.highlights);
      toast.success("Blickfång framtagna ur ditt CV");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setHlLoading(false); }
  };

  const useHook = () => {
    if (!highlights?.hook) return;
    setField("professional_profile", highlights.hook);
    toast.success("Ingressen infogad i professionell profil");
  };

  const openQc = async () => {
    setQcOpen(true);
    setQcLoading(true);
    try {
      await save({}, true);
      const { data } = await api.get(`/cvs/${id}/qc`);
      setQc(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setQcLoading(false); }
  };

  useEffect(() => {
    api.get(`/cvs/${id}`).then((r) => {
      setCv(r.data);
      setC(r.data.edited_version || r.data.ai_version || {});
      setContact(r.data.contact || {});
      setTemplate(r.data.template || "modern");
      setAccentColor(r.data.accent_color || "");
      setLayout(r.data.layout === "sidebar" || r.data.layout === "standard" ? r.data.layout : (r.data.template === "sidebar" ? "sidebar" : "standard"));
      setPhotoData(r.data.photo_data || null);
      setHighlights(r.data.highlights || null);
    });
  }, [id]);

  const setField = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async (extra = {}, silent = false) => {
    setSaving(true);
    try {
      await api.put(`/cvs/${id}`, { edited_version: c, contact, template, accent_color: accentColor || null, layout, ...extra });
      if (!silent) toast.success("Sparat");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const approve = async () => { await save({ status: "final" }, true); setCv((p) => ({ ...p, status: "final" })); toast.success("Slutversion godkänd"); };

  const downloadPdf = async () => {
    await save({}, true);
    setDl("pdf");
    try {
      const name = `${cv.company || "CV"}-${cv.title || ""}`.replace(/\s+/g, "_");
      await downloadCvPdf(id, `HAMN_CV_${name}.pdf`);
    } catch (e) { toast.error(e.message || formatApiError(e.response?.data?.detail)); }
    finally { setDl(null); }
  };

  const downloadDocx = async () => {
    await save({}, true);
    setDl("docx");
    try {
      const name = `${cv.company || "CV"}-${cv.title || ""}`.replace(/\s+/g, "_");
      await downloadCvDocx(id, `HAMN_CV_${name}.docx`);
    } catch (e) { toast.error(e.message || formatApiError(e.response?.data?.detail)); }
    finally { setDl(null); }
  };

  const resetAi = async () => {
    await api.post(`/cvs/${id}/reset`);
    const { data } = await api.get(`/cvs/${id}`);
    setC(data.ai_version); toast.success("Återställt till AI-versionen");
  };

  // work experience helpers
  const exps = Array.isArray(c?.work_experience) ? c.work_experience : [];
  const setExps = (arr) => setField("work_experience", arr);
  const updateExp = (i, k, v) => setExps(exps.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  const moveExp = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= exps.length) return;
    const arr = [...exps]; [arr[i], arr[j]] = [arr[j], arr[i]]; setExps(arr);
  };
  const addExp = () => setExps([...exps, { employer: "", role: "", period: "", bullets: [] }]);
  const removeExp = (i) => setExps(exps.filter((_, j) => j !== i));

  const edus = Array.isArray(c?.education) ? c.education : [];
  const updateEdu = (i, k, v) => setField("education", edus.map((e, j) => (j === i ? { ...e, [k]: v } : e)));

  if (!cv || !c) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  const previewCv = { ...cv, edited_version: c, contact, template, accent_color: accentColor || null, layout, photo_data: photoData };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/cvs/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoData(data.photo_data);
      toast.success("Foto uppladdat");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };
  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.delete(`/cvs/${id}/photo`);
      setPhotoData(null);
      toast.success("Foto borttaget");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };

  const assistantSend = async (message) => {
    const { data } = await api.post(`/cvs/${id}/assistant`, { message });
    return data;
  };
  const assistantHistory = async () => {
    const { data } = await api.get(`/cvs/${id}/assistant`);
    return data;
  };
  const addToList = (key, value) => {
    const cur = Array.isArray(c[key]) ? c[key] : [];
    if (cur.map((x) => String(x).toLowerCase()).includes(String(value).toLowerCase())) { toast.info("Finns redan i CV:t"); return; }
    setField(key, [...cur, value]);
    toast.success("Tillagt i CV:t");
  };
  const applyProposal = (p) => {
    switch (p.type) {
      case "profile": setField("professional_profile", p.value); toast.success("Professionell profil uppdaterad"); break;
      case "competency": addToList("core_competencies", p.value); break;
      case "strength": addToList("personal_strengths", p.value); break;
      case "system": addToList("systems_tools", p.value); break;
      case "language": addToList("languages", p.value); break;
      case "certification": addToList("certifications", p.value); break;
      case "experience_bullet": {
        const idx = Number(p.exp_index);
        const arr = Array.isArray(c.work_experience) ? [...c.work_experience] : [];
        if (!arr[idx]) { toast.error("Hittade inte erfarenheten – lägg till punkten manuellt"); return; }
        const bullets = Array.isArray(arr[idx].bullets) ? arr[idx].bullets : [];
        arr[idx] = { ...arr[idx], bullets: [...bullets, p.value] };
        setField("work_experience", arr); toast.success("Punkt tillagd under erfarenheten"); break;
      }
      case "experience": {
        const v = p.value || {};
        const arr = Array.isArray(c.work_experience) ? c.work_experience : [];
        setField("work_experience", [...arr, { role: v.role || "", employer: v.employer || "", period: v.period || "", bullets: Array.isArray(v.bullets) ? v.bullets : [] }]);
        toast.success("Ny erfarenhet tillagd"); break;
      }
      default: break;
    }
  };

  const assistantSuggestions = [
    "Vad saknas i mitt CV jämfört med min profil?",
    "Lyft fram mina viktigaste erfarenheter tydligare",
    "Gör min professionella profil starkare",
    "Hjälp mig formulera mina punkter mer konkret",
  ];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap no-print">
        <Link to="/app/cv"><Button variant="ghost" className="rounded-full" data-testid="back-to-cvs"><ArrowLeft className="h-4 w-4 mr-2" /> Mina CV:n</Button></Link>
        <div className="flex gap-2 flex-wrap">
          <AiAssistant
            triggerLabel="AI-hjälp"
            triggerVariant="default"
            triggerTestId="cv-ai-assistant-trigger"
            title="AI-hjälp med ditt CV"
            intro="Jag jämför ditt CV med din profil, föreslår vad som kan läggas till och hjälper dig formulera. Du godkänner varje ändring."
            suggestions={assistantSuggestions}
            loadHistory={assistantHistory}
            send={assistantSend}
            onApply={applyProposal}
          />
          <Button variant="outline" className="rounded-full" onClick={resetAi} data-testid="reset-ai-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Återställ AI-version</Button>
          <Link to={`/cv-print/${id}`} target="_blank"><Button variant="outline" className="rounded-full" data-testid="print-btn"><Printer className="h-4 w-4 mr-1.5" /> Utskrift / A4</Button></Link>
          <Button variant="outline" className="rounded-full" onClick={openQc} data-testid="qc-btn"><ClipboardCheck className="h-4 w-4 mr-1.5" /> Kvalitetskontroll</Button>
          <Button variant="outline" className="rounded-full" onClick={downloadPdf} disabled={dl !== null} data-testid="download-pdf-btn">{dl === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : (cv.locked ? <Lock className="h-4 w-4 mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />)} Ladda ner PDF</Button>
          <Button variant="outline" className="rounded-full" onClick={downloadDocx} disabled={dl !== null} data-testid="download-docx-btn">{dl === "docx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : (cv.locked ? <Lock className="h-4 w-4 mr-1.5" /> : <FileType className="h-4 w-4 mr-1.5" />)} Ladda ner Word</Button>
          <Button className="rounded-full" onClick={() => save()} disabled={saving} data-testid="save-cv-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Spara</Button>
          <Button className="rounded-full bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90" onClick={approve} data-testid="approve-cv-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Godkänn slutversion</Button>
          {cv.apply_url && (
            <a href={cv.apply_url} target="_blank" rel="noopener noreferrer" data-testid="apply-to-employer-link">
              <Button className="rounded-full" variant="outline"><ExternalLink className="h-4 w-4 mr-1.5" /> Ansök hos arbetsgivaren</Button>
            </a>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 text-[hsl(36,60%,30%)] px-4 py-3 mb-6 flex items-center gap-2 text-sm no-print" data-testid="ai-draft-banner">
        <AlertTriangle className="h-4 w-4 shrink-0" /> AI-genererat utkast – granska alltid innan användning.
      </div>

      {cv.locked && (
        <div className="mb-6 no-print" data-testid="cv-locked-banner">
          <div className="rounded-xl bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 mb-4 flex items-start gap-2 text-sm">
            <Lock className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Det här CV:t är sparat men låst för nedladdning. Betala nedan med kort, Apple Pay, Google Pay, Link eller Klarna – så låses det upp automatiskt direkt.</span>
          </div>
          <SwishQuickPay />
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Editor */}
        <div className="space-y-5 no-print">
          <Card className="rounded-2xl border-border p-6 space-y-5">
            <h3 className="font-head font-semibold text-lg">Utseende</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mall (typografi)</Label>
                <Select value={template} onValueChange={setTemplate}>
                  <SelectTrigger data-testid="template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="klassisk">Klassisk</SelectItem>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="kompakt">Kompakt</SelectItem>
                    <SelectItem value="grafisk">Grafisk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Layout</Label>
                <div className="flex gap-2">
                  <Button type="button" variant={layout === "standard" ? "default" : "outline"} className="rounded-full flex-1 h-10" onClick={() => setLayout("standard")} data-testid="layout-standard">Enkel</Button>
                  <Button type="button" variant={layout === "sidebar" ? "default" : "outline"} className="rounded-full flex-1 h-10" onClick={() => setLayout("sidebar")} data-testid="layout-sidebar">Sidokolumn</Button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Accentfärg</Label>
              <div className="flex flex-wrap items-center gap-2.5" data-testid="accent-color-picker">
                {ACCENT_PRESETS.map((col) => {
                  const active = (accentColor || "").toUpperCase() === col.hex.toUpperCase();
                  return (
                    <button key={col.hex} type="button" title={col.name} aria-label={col.name}
                      onClick={() => setAccentColor(col.hex)} data-testid={`accent-${col.hex.replace('#','')}`}
                      className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${active ? "ring-2 ring-offset-2 ring-foreground scale-110" : ""}`}
                      style={{ backgroundColor: col.hex }} />
                  );
                })}
                <label className="relative h-8 w-8 rounded-full overflow-hidden ring-1 ring-border cursor-pointer inline-flex items-center justify-center bg-gradient-to-br from-pink-400 via-amber-300 to-sky-400" title="Egen färg" data-testid="accent-custom-wrap">
                  <Plus className="h-4 w-4 text-white drop-shadow" />
                  <input type="color" value={accentColor || "#2E5C4B"} onChange={(e) => setAccentColor(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer" data-testid="accent-custom-input" />
                </label>
                <Input value={accentColor || ""} onChange={(e) => setAccentColor(e.target.value)} placeholder="#RRGGBB"
                  className="h-8 w-28 text-sm" data-testid="accent-hex-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Profilfoto <span className="text-xs text-muted-foreground font-normal">(visas i sidokolumn-layouten)</span></Label>
              <div className="flex items-center gap-3" data-testid="cv-photo-control">
                {photoData ? (
                  <img src={photoData} alt="" className="h-14 w-14 rounded-full object-cover ring-1 ring-border" data-testid="cv-photo-preview" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><Camera className="h-5 w-5" /></div>
                )}
                <label>
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} data-testid="cv-photo-input" />
                  <span className="inline-flex items-center rounded-full border border-border px-3 py-1.5 text-sm cursor-pointer hover:border-primary/40" data-testid="cv-photo-upload-btn">
                    {photoBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />} {photoData ? "Byt foto" : "Ladda upp foto"}
                  </span>
                </label>
                {photoData && (
                  <Button variant="ghost" size="sm" className="rounded-full text-destructive" onClick={removePhoto} disabled={photoBusy} data-testid="cv-photo-remove-btn">Ta bort</Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[["full_name", "Namn"], ["email", "E-post"], ["phone", "Telefon"], ["city", "Ort"]].map(([k, lbl]) => (
                <div key={k} className="space-y-2">
                  <Label>{lbl}</Label>
                  <Input data-testid={`contact-${k}`} value={contact[k] || ""} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} />
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border-border p-6 space-y-4">
            <h3 className="font-head font-semibold text-lg">Professionell profil</h3>
            <Textarea data-testid="edit-profile" rows={4} value={c.professional_profile || ""} onChange={(e) => setField("professional_profile", e.target.value)} />
          </Card>

          {refStrengths.length > 0 && (
            <Card className="rounded-2xl border-border p-6 space-y-3" data-testid="ref-strengths-card">
              <div className="flex items-center gap-2"><Quote className="h-5 w-5 text-primary" /><h3 className="font-head font-semibold text-lg">Referensbekräftade styrkor</h3></div>
              <p className="text-sm text-muted-foreground">Lägg in en styrka som en referens bekräftat – med källhänvisning – direkt i ditt CV (under Verifierat av referenser).</p>
              <ul className="space-y-2">
                {refStrengths.map((s, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 rounded-xl border border-border p-3" data-testid={`ref-strength-${i}`}>
                    <div className="text-sm min-w-0">
                      <div className="font-medium">{s.dimension}</div>
                      <div className="text-muted-foreground">”{s.text}” <span className="text-xs">— {s.name}</span></div>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => insertRefStrength(s)} data-testid={`insert-ref-strength-${i}`}>Lägg till i CV</Button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card className="rounded-2xl border-primary/25 bg-primary/[0.03] p-6 space-y-4" data-testid="highlights-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-head font-semibold text-lg flex items-center gap-2"><Lightbulb className="h-5 w-5 text-primary" /> Det här fångar läsaren</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">AI lyfter fram det mest intresseväckande ur ditt CV – helt utifrån dina verkliga uppgifter.</p>
              </div>
              <Button variant="outline" className="rounded-full shrink-0" onClick={genHighlights} disabled={hlLoading} data-testid="gen-highlights-btn">
                {hlLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {highlights ? "Ta fram igen" : "Hitta blickfång"}
              </Button>
            </div>
            {highlights && (
              <div className="space-y-4" data-testid="highlights-content">
                {highlights.hook && (
                  <div className="rounded-xl bg-white border border-border p-4">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Föreslagen ingress</div>
                    <p className="text-sm leading-relaxed">{highlights.hook}</p>
                    <Button size="sm" variant="ghost" className="rounded-full text-primary mt-2 px-2" onClick={useHook} data-testid="use-hook-btn">
                      <ArrowLeft className="h-3.5 w-3.5 mr-1 rotate-90" /> Använd som ingress i profilen
                    </Button>
                  </div>
                )}
                {(highlights.points || []).length > 0 && (
                  <ul className="space-y-2">
                    {highlights.points.map((p, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span><span className="font-medium">{p.title}:</span> <span className="text-muted-foreground">{p.text}</span></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>

          <Card className="rounded-2xl border-border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-head font-semibold text-lg">Arbetslivserfarenhet</h3>
              <Button size="sm" variant="outline" className="rounded-full" onClick={addExp} data-testid="add-exp-btn"><Plus className="h-4 w-4 mr-1" /> Lägg till</Button>
            </div>
            <div className="space-y-4">
              {exps.map((e, i) => (
                <div key={i} className={`rounded-xl border p-4 ${e.hidden ? "opacity-50 border-dashed" : "border-border"}`} data-testid={`exp-edit-${i}`}>
                  <div className="flex items-center justify-end gap-1 mb-2">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveExp(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveExp(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`toggle-exp-${i}`} onClick={() => updateExp(i, "hidden", !e.hidden)}>{e.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeExp(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Roll" value={e.role || ""} onChange={(ev) => updateExp(i, "role", ev.target.value)} />
                    <Input placeholder="Arbetsgivare" value={e.employer || ""} onChange={(ev) => updateExp(i, "employer", ev.target.value)} />
                  </div>
                  <Input className="mt-3" placeholder="Tidsperiod" value={e.period || ""} onChange={(ev) => updateExp(i, "period", ev.target.value)} />
                  <Textarea className="mt-3" rows={3} placeholder="En punkt per rad" value={(e.bullets || []).join("\n")} onChange={(ev) => updateExp(i, "bullets", ev.target.value.split("\n").filter((x) => x.trim()))} />
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border-border p-6 space-y-4">
            <h3 className="font-head font-semibold text-lg">Utbildning</h3>
            {edus.map((e, i) => (
              <div key={i} className="grid grid-cols-2 gap-3 border-b border-border pb-3">
                <Input placeholder="Utbildning" value={e.name || ""} onChange={(ev) => updateEdu(i, "name", ev.target.value)} />
                <Input placeholder="Skola" value={e.school || ""} onChange={(ev) => updateEdu(i, "school", ev.target.value)} />
                <Input placeholder="År" value={e.period || ""} onChange={(ev) => updateEdu(i, "period", ev.target.value)} />
                <Input placeholder="Not" value={e.note || ""} onChange={(ev) => updateEdu(i, "note", ev.target.value)} />
              </div>
            ))}
            {edus.length === 0 && <p className="text-sm text-muted-foreground">Ingen utbildning i CV:t.</p>}
          </Card>

          <Card className="rounded-2xl border-border p-6 space-y-5">
            <h3 className="font-head font-semibold text-lg">Övriga sektioner</h3>
            {TAG_SECTIONS.map(([key, label]) => (
              <FieldRenderer key={key} field={{ name: key, label, type: "tags", placeholder: "Skriv och tryck Enter" }} value={c[key]} onChange={(v) => setField(key, v)} />
            ))}
          </Card>
        </div>

        {/* Live preview */}
        <div>
          <div className="lg:sticky lg:top-6">
            <div className="text-sm text-muted-foreground mb-2 no-print">Förhandsgranskning</div>
            <Card className="rounded-2xl border-border p-8 shadow-sm overflow-hidden">
              <CVDocument cv={previewCv} template={template} />
            </Card>
          </div>
        </div>
      </div>

      {/* Nästa steg: personligt brev */}
      <Card className="rounded-2xl border-primary/25 bg-primary/[0.05] p-6 mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 no-print" data-testid="cv-cover-letter-prompt">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"><Mail className="h-5 w-5" /></div>
          <div>
            <div className="font-head font-semibold">Klar med ditt CV? Vill du skapa ett personligt brev?</div>
            <p className="text-sm text-muted-foreground mt-0.5">Vi skriver ett träffsäkert brev till samma jobb, grundat på din profil. Du redigerar och exporterar precis som CV:t.</p>
          </div>
        </div>
        <Button
          className="rounded-full shrink-0"
          data-testid="cv-create-cover-letter-btn"
          onClick={() => {
            localStorage.setItem("hamn_prefill_letter", JSON.stringify({
              application_id: cv.application_id || null, company: cv.company || "", title: cv.title || "", url: cv.url || "", template: template || cv.template || "modern",
            }));
            navigate("/app/personligt-brev");
          }}
        >
          Skapa personligt brev <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </Card>


      <Dialog open={qcOpen} onOpenChange={setQcOpen}>
        <DialogContent className="max-w-lg" data-testid="qc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" /> Kvalitetskontroll före export</DialogTitle>
            <DialogDescription>En snabb genomgång innan du laddar ner ditt CV. Detta ändrar inget – det hjälper dig fånga det som saknas.</DialogDescription>
          </DialogHeader>
          {qcLoading || !qc ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-3">
              {qc.ready ? (
                <div className="rounded-xl bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 px-4 py-3 text-sm flex items-center gap-2 text-[hsl(150,40%,28%)]" data-testid="qc-ready">
                  <CheckCircle2 className="h-4 w-4" /> Allt ser bra ut – ditt CV är redo att exporteras.
                </div>
              ) : (
                <div className="rounded-xl bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 px-4 py-3 text-sm flex items-center gap-2 text-[hsl(36,60%,30%)]" data-testid="qc-warnings">
                  <AlertTriangle className="h-4 w-4" /> {qc.warnings} sak(er) att se över innan export.
                </div>
              )}
              <ul className="space-y-2" data-testid="qc-checks">
                {qc.checks.map((chk, i) => {
                  const Icon = chk.status === "pass" ? CircleCheck : chk.status === "warn" ? CircleAlert : chk.status === "info" ? ShieldCheck : Info;
                  const color = chk.status === "pass" ? "text-[hsl(var(--success))]" : chk.status === "warn" ? "text-[hsl(36,70%,45%)]" : "text-primary";
                  return (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                      <div>
                        <div className="font-medium">{chk.label}</div>
                        {chk.status === "warn" && chk.hint && <div className="text-xs text-muted-foreground">{chk.hint}</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="rounded-full" onClick={() => { setQcOpen(false); downloadPdf(); }} disabled={dl !== null} data-testid="qc-download-pdf-btn"><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
                <Button className="rounded-full" onClick={() => { setQcOpen(false); downloadDocx(); }} disabled={dl !== null} data-testid="qc-download-docx-btn"><FileType className="h-4 w-4 mr-1.5" /> Word</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
